import { useCallback, useEffect, useRef, useState } from "react";
import { getMatchDictionary, MatchStatus, SPORT } from "service";

export type MatchDictionary = Partial<Record<SPORT, MatchStatus[]>>;

type MatchDictionaryResponse = {
  dictionary: MatchDictionary;
  isLoading: boolean;
  isRefreshing: boolean;
};

const DICTIONARY_POLL_INTERVAL = 10_000;

export const useMatchDictionary = (sport?: SPORT): MatchDictionaryResponse => {
  const [dictionary, setDictionary] = useState<MatchDictionary>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const isInitialLoad = useRef(true);
  const isFetching = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const normalizeDictionary = useCallback((body: unknown): MatchDictionary => {
    const source = (body || {}) as Record<string, unknown>;
    return Object.entries(source).reduce<MatchDictionary>(
      (acc, [key, value]) => {
        const list = Array.isArray(value) ? value : [value];
        acc[Number(key) as SPORT] = list.filter(
          (item) => item !== undefined && item !== null
        ) as MatchStatus[];
        return acc;
      },
      {}
    );
  }, []);

  const fetchDictionary = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    const initialLoad = isInitialLoad.current;
    if (initialLoad) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const { body } = await getMatchDictionary(sport);
      if (!isMounted.current) return;
      setDictionary(normalizeDictionary(body));
    } catch (error) {
      console.warn("Unable to fetch match dictionary", error);
    } finally {
      isFetching.current = false;
      if (!isMounted.current) return;
      if (initialLoad) {
        isInitialLoad.current = false;
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [normalizeDictionary, sport]);

  useEffect(() => {
    fetchDictionary();
    const intervalId = setInterval(fetchDictionary, DICTIONARY_POLL_INTERVAL);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchDictionary]);

  return { dictionary, isLoading, isRefreshing };
};
